/**
 * Therapist Session contract — CORE-05A, TSESSION-001…069.
 *
 * Hand-written Zod following `rider-session.ts`, and not a substitute for the
 * generated DB types: this is the subset the client and the Edge Function agree
 * on. Server state a therapist never receives — cycle-entry ordering keys, the
 * Auto Secure processing position, the count-lock emit timestamp — is absent by
 * design.
 *
 * Three deliberate absences, each of which would be a bug to "fix":
 *
 * 1. **No `dispatchAreaId` on any request.** A therapist's Dispatch Area is
 *    frozen at cycle entry (TSESSION-006) and resolved server-side from her own
 *    member row. A client that could send an area could send another one, and
 *    TSESSION-036 uses area to decide who may see a Trade.
 *
 * 2. **No `rank` on any request.** Same reason — frozen at entry (TSESSION-042),
 *    and it decides the rank minimum. It travels outbound only.
 *
 * 3. **No visibility filter on the Trade list.** TSESSION-037 restricts a
 *    request to therapists whose Off date matches, and that lives in the RLS
 *    policy (0076). There is no read route and no filter field, so the rule has
 *    exactly one implementation.
 */

import { z } from "zod";

const uuidSchema = z.string().uuid();
const offsetDateTimeSchema = z.string().datetime({ offset: true });

/** A Manila calendar service date. */
export const therapistSessionDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected an ISO date (YYYY-MM-DD)")
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, "Expected a valid calendar date");

/**
 * TSESSION-012…015. Identical values to `riderRankSchema`, deliberately NOT the
 * same enum: therapist rank and rider rank are independent facts about different
 * roles, and sharing a type would imply they must move together.
 *
 * ⚠ How a therapist's rank is SET is undefined in the vault — OQ-80. Nothing in
 * this contract or the schema behind it computes one.
 */
export const therapistRankSchema = z.enum(["bronze", "silver", "gold", "diamond"]);

/** §6.1 cycle states. `finalized` is reached only by the Friday job. */
export const therapistCycleStatusSchema = z.enum([
  "not_open",
  "priority_open",
  "regular_open",
  "securing_closed",
  "finalized",
]);

/**
 * §6.3 date states. Draft before Friday 7:00 PM, final after — the distinction
 * is what makes Trade unavailable pre-finalization (TSESSION-036) without a
 * second flag to keep in sync.
 */
export const therapistDateStateSchema = z.enum([
  "draft_secured",
  "draft_off",
  "secured_final",
  "off_final",
]);

/** TSESSION-003: the four ways a Secured Session can come about. */
export const therapistSecureSourceSchema = z.enum([
  "selection",
  "approved_exception",
  "manual_auto_secure",
  "automatic_auto_secure",
  /** Acquired from another therapist post-finalization — TSESSION-039. */
  "trade",
]);

/** §6.2 approval-request states, including the two non-decisions. */
export const therapistApprovalStateSchema = z.enum([
  "pending",
  "approved",
  "declined",
  /** Unresolved at Thursday 6:00 PM — TSESSION-023. */
  "auto_declined",
  /** Withdrawn by the therapist — preserved for audit, TSESSION-049. */
  "cancelled",
  /** Withdrawn by Manual Auto Secure as one atomic action — TSESSION-050. */
  "withdrawn_for_auto_secure",
]);

/** Why a proposal cannot take the normal path — TSESSION-020. */
export const therapistApprovalReasonSchema = z.enum([
  "below_rank_minimum",
  "omits_saturday",
  "omits_sunday",
  "exceeds_off_limit",
]);

/**
 * TSESSION-036 names two request directions. Only `secured_to_off` is reachable
 * in this release — see OQ-81: no screen in the approved set has UI for the
 * Off-side request, and under manual Accept Trade it has no specified audience.
 * The value exists so the column does not have to be widened later.
 */
export const therapistTradeDirectionSchema = z.enum(["secured_to_off", "off_to_secured"]);

/** TSESSION-057 + the Secured tab's status list in the navigation spec. */
export const therapistTradeStateSchema = z.enum(["pending", "accepted", "expired", "cancelled"]);

// ── outbound row shapes ─────────────────────────────────────────────────────

/** One of the seven date states a therapist holds for a cycle. */
export const therapistSessionSchema = z.object({
  id: uuidSchema,
  cycleId: uuidSchema,
  serviceDate: therapistSessionDateSchema,
  state: therapistDateStateSchema,
  source: therapistSecureSourceSchema.nullable(),
  /** Fixed 2:00 PM → midnight for every hub — TSESSION-038/056. */
  startsAt: offsetDateTimeSchema,
  endsAt: offsetDateTimeSchema,
});

/** The therapist's own membership of one cycle, with the frozen snapshot. */
export const therapistCycleMemberSchema = z.object({
  cycleId: uuidSchema,
  weekStartDate: therapistSessionDateSchema,
  status: therapistCycleStatusSchema,
  /** Frozen at entry — TSESSION-006/042. */
  rankAtEntry: therapistRankSchema,
  /** Frozen at entry. Modelled as `hub_id` for this release — OQ-67. */
  hubIdAtEntry: uuidSchema,
  /** True once a batch exists; the cycle selection is then immutable — TSESSION-018. */
  hasSecuredBatch: z.boolean(),
  secureSource: therapistSecureSourceSchema.nullable(),
});

export const therapistApprovalRequestSchema = z.object({
  id: uuidSchema,
  cycleId: uuidSchema,
  state: therapistApprovalStateSchema,
  /** The exact seven-date proposal. Approval secures THIS — TSESSION-022. */
  proposedDates: z.array(therapistSessionDateSchema).length(7),
  selectedDates: z.array(therapistSessionDateSchema),
  reasons: z.array(therapistApprovalReasonSchema).min(1),
  /** Rolling 6:00 PM, capped at Thursday — TSESSION-023. */
  decisionDeadlineAt: offsetDateTimeSchema,
  submittedAt: offsetDateTimeSchema,
  decidedAt: offsetDateTimeSchema.nullable(),
});

/**
 * A Trade request as a therapist sees it.
 *
 * ⚠ Carries NO requester identity. TSESSION-057 lists date, time and Dispatch
 * Area as the confirmation contents and nothing else, the approved screens show
 * no name, and the rider precedent shows none either. Adding one is a
 * disclosure decision, not a field.
 */
export const therapistTradeRequestSchema = z.object({
  id: uuidSchema,
  serviceDate: therapistSessionDateSchema,
  direction: therapistTradeDirectionSchema,
  state: therapistTradeStateSchema,
  /** Display label for the frozen Dispatch Area. */
  dispatchArea: z.string(),
  startsAt: offsetDateTimeSchema,
  endsAt: offsetDateTimeSchema,
  /** Noon Manila on the service date — TSESSION-038. */
  expiresAt: offsetDateTimeSchema,
  createdAt: offsetDateTimeSchema,
});

// ── requests ────────────────────────────────────────────────────────────────
// Every schema is `.strict()`. A therapist sends what she chose and nothing
// else; deadlines, areas, ranks and counts are all server-resolved.

/**
 * The seven-date batch — TSESSION-017.
 *
 * `selectedDates` carries only the Secured dates; the Off dates are the
 * complement and are derived server-side. Sending both would let a client
 * describe a week that is not seven days, and the atomic commit would then have
 * to decide which half it believed.
 */
export const secureTherapistSessionsSchema = z
  .object({
    cycleId: uuidSchema,
    selectedDates: z
      .array(therapistSessionDateSchema)
      .min(1)
      .max(7)
      .refine((d) => new Set(d).size === d.length, "Dates must be unique"),
  })
  .strict();

/** Manual Auto Secure — TSESSION-027. No dates: the server picks all six. */
export const autoSecureTherapistSessionsSchema = z.object({ cycleId: uuidSchema }).strict();

/** Request for Approval — TSESSION-020/022. Same shape, different route. */
export const submitTherapistApprovalSchema = z
  .object({
    cycleId: uuidSchema,
    selectedDates: z
      .array(therapistSessionDateSchema)
      .min(0)
      .max(7)
      .refine((d) => new Set(d).size === d.length, "Dates must be unique"),
  })
  .strict();

export const cancelTherapistApprovalSchema = z.object({ requestId: uuidSchema }).strict();

/**
 * Admin decision — TSESSION-022.
 *
 * There is no `dates` field, and that is the whole point of the rule: *"Approval
 * secures exactly the dates stored in the request. Admin must not add or replace
 * dates automatically."* An approver who could send dates could rewrite the
 * proposal, and the therapist would be committed to a week she did not choose.
 */
export const decideTherapistApprovalSchema = z
  .object({
    requestId: uuidSchema,
    decision: z.enum(["approve", "decline"]),
    /** Required on a decline so the therapist is told something. */
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .strict()
  .refine((v) => v.decision === "approve" || Boolean(v.reason), {
    message: "A declined request must carry a reason",
    path: ["reason"],
  });

/** Post-finalization only — TSESSION-036. Ids alone; the deadline is derived. */
export const requestTherapistSessionTradeSchema = z
  .object({ therapistSessionId: uuidSchema })
  .strict();

/**
 * Start a Secured date — TEND-008/009.
 *
 * The id alone. No `startedAt`: the instant is the SERVER's, because the whole
 * gate is a comparison against it (the 1:00 PM window, PreStartWaiting, and
 * whether this is a Late Start). A client-supplied timestamp would let a phone
 * with a wrong clock — or a tampered one — start a session outside its window
 * and rewrite its own attendance record.
 *
 * No equipment either: TEND-012's four toggles are not modelled in this schema
 * yet, so there is nothing for them to filter (0085's header).
 *
 * `lat`/`lng` ARE required (0097). TEND-009 lists Location Services among the
 * Start requirements, and it is the only one of the four the server can actually
 * hold — a client cannot prove notifications are enabled, but it cannot fake
 * having sent a coordinate. The fix seeds `staff_live_locations`, because
 * `clock_in_therapist` used to be the only writer of a therapist pin and an
 * unlocated therapist is excluded from ASAP with `LOCATION_UNKNOWN` (OQ-61).
 *
 * ⚠ This is NOT a geofence. TEND-010 forbids validating 1 km Hub proximity or
 * presence inside the Dispatch Area at Start; the coordinate is recorded, never
 * compared against a radius. See 0097's header before adding any distance test.
 */
export const startTherapistSessionSchema = z
  .object({
    therapistSessionId: uuidSchema,
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    /**
     * 0357 — a point she chose for her session Origin (where the rider brings
     * her back to). Absent = her Start fix, 0138's rule. `lat`/`lng` above stay
     * her REAL position either way: they seed the live pin ASAP ranks by.
     */
    origin: z
      .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
      .strict()
      .optional(),
  })
  .strict();

export const cancelTherapistSessionTradeSchema = z.object({ tradeId: uuidSchema }).strict();

export const acceptTherapistSessionTradeSchema = z.object({ tradeId: uuidSchema }).strict();

/**
 * Friday 7:00 PM finalization — TSESSION-031. Reachable by pg_cron and by a
 * superadmin who needs it now.
 *
 * `cycleId` is explicit and never defaulted from the clock. `0068` is the
 * cautionary tale: a job whose target day resolved from a UTC `current_date`
 * silently computed the wrong day for eight hours of every Manila night.
 */
export const finalizeTherapistCycleSchema = z.object({ cycleId: uuidSchema }).strict();

// ── responses ───────────────────────────────────────────────────────────────

export const secureTherapistSessionsResultSchema = z.object({
  cycleId: uuidSchema,
  source: therapistSecureSourceSchema,
  sessions: z.array(therapistSessionSchema).length(7),
});

export const finalizeTherapistCycleResultSchema = z.object({
  cycleId: uuidSchema,
  /** Therapists Auto Secured by this run — 0 on a re-run, which is success. */
  autoSecuredCount: z.number().int().min(0),
  /** One immutable lock per service date and Dispatch Area — TSESSION-033. */
  countLocks: z.array(
    z.object({
      serviceDate: therapistSessionDateSchema,
      hubId: uuidSchema,
      finalTherapistCount: z.number().int().min(0),
    }),
  ),
  /** True when the cycle was already finalized — the retry is a no-op, not an error. */
  alreadyFinalized: z.boolean(),
});

/**
 * Wire codes. Like the rider lifecycle, most refusals here are `check_violation`
 * and arrive from Postgres in the DETAIL field rather than the SQLSTATE — every
 * value below must exist in exactly one `raise ... detail = '...'`, and a raise
 * without one reaches the client as an unmapped 500.
 *
 * ⚠ `TRADE_NOT_AVAILABLE` is deliberately one code covering already-accepted,
 * expired, cancelled, and never-visible-to-you. TSESSION-037 decides who may see
 * a request; splitting the refusal would hand that distinction back to any
 * client that switches on it — the same reasoning as `SESSION_ALREADY_SECURED`
 * on the rider side.
 */
export const therapistSessionErrorCodeSchema = z.enum([
  // ── cycle access ──────────────────────────────────────────────────────────
  "CYCLE_NOT_FOUND",
  /** Before Monday, or Diamond-only and this therapist is not — TSESSION-007/008. */
  "CYCLE_NOT_OPEN",
  /** Past Friday 7:00 PM — TSESSION-011. */
  "SECURING_CLOSED",
  /** Not an active member of this cycle — TSESSION-043. */
  "NOT_IN_CYCLE",

  // ── securing ──────────────────────────────────────────────────────────────
  /** TSESSION-018: once secured, the cycle selection cannot be edited. */
  "BATCH_ALREADY_SECURED",
  /** The proposal needs approval — the client should route, not retry. */
  "APPROVAL_REQUIRED",
  /** A date outside the cycle's seven, or a duplicate. */
  "INVALID_PROPOSAL",

  // ── approvals ─────────────────────────────────────────────────────────────
  /** TSESSION-048: one pending request per therapist per cycle. */
  "APPROVAL_ALREADY_PENDING",
  "APPROVAL_NOT_FOUND",
  /** TSESSION-052: another valid action committed first; the saved result stands. */
  "APPROVAL_ALREADY_RESOLVED",
  /** Past Thursday 6:00 PM — TSESSION-009/023. */
  "APPROVAL_CUTOFF_PASSED",

  // ── trade ─────────────────────────────────────────────────────────────────
  /** TSESSION-036: Trade exists only after Friday finalization. */
  "CYCLE_NOT_FINALIZED",
  /** Not a Secured date of this therapist's. */
  "SESSION_NOT_YOURS",
  /** One open request per session at a time. */
  "TRADE_ALREADY_OPEN",
  /** Accepted, expired, cancelled, or never visible to you — see above. */
  "TRADE_NOT_AVAILABLE",
  /** TSESSION-037: the acceptor holds no Off Session on that date and area. */
  "TRADE_NOT_ELIGIBLE",
  /** Past noon on the service date — TSESSION-038. */
  "TRADE_EXPIRED",

  // ── finalization (superadmin / cron only) ─────────────────────────────────
  /**
   * Finalization was called before Friday 7:00 PM. The superadmin route exists
   * for a MISSED Friday, which is always after the cutoff — never before it.
   */
  "SECURING_STILL_OPEN",
  /**
   * A cycle member does not hold seven date states, so no count lock was
   * written. CORE-05A §4.10 gates the count on every therapist being resolved;
   * locking an incomplete week is unrecoverable, because the lock is immutable
   * and Saturday's Rider generation would believe it.
   */
  "FINALIZATION_INCOMPLETE",

  // ── SOS (0273) ────────────────────────────────────────────────────────────
  /** Not this therapist's SOS, or — for a Hub Manager — another hub's. One code for both. */
  "SOS_NOT_FOUND",
  /** The danger type is set once; the same type again is a retry, not this error. */
  "SOS_TYPE_ALREADY_SET",
  /** 0277 — an SOS is acknowledged before it can be resolved. */
  "SOS_NOT_ACKNOWLEDGED",
]);

export type TherapistRank = z.infer<typeof therapistRankSchema>;
export type TherapistCycleStatus = z.infer<typeof therapistCycleStatusSchema>;
export type TherapistDateState = z.infer<typeof therapistDateStateSchema>;
export type TherapistSecureSource = z.infer<typeof therapistSecureSourceSchema>;
export type TherapistApprovalState = z.infer<typeof therapistApprovalStateSchema>;
export type TherapistApprovalReason = z.infer<typeof therapistApprovalReasonSchema>;
export type TherapistTradeDirection = z.infer<typeof therapistTradeDirectionSchema>;
export type TherapistTradeState = z.infer<typeof therapistTradeStateSchema>;
export type TherapistSession = z.infer<typeof therapistSessionSchema>;
export type TherapistCycleMember = z.infer<typeof therapistCycleMemberSchema>;
export type TherapistApprovalRequest = z.infer<typeof therapistApprovalRequestSchema>;
export type TherapistTradeRequest = z.infer<typeof therapistTradeRequestSchema>;
export type SecureTherapistSessionsInput = z.infer<typeof secureTherapistSessionsSchema>;
export type AutoSecureTherapistSessionsInput = z.infer<typeof autoSecureTherapistSessionsSchema>;
export type SubmitTherapistApprovalInput = z.infer<typeof submitTherapistApprovalSchema>;
export type CancelTherapistApprovalInput = z.infer<typeof cancelTherapistApprovalSchema>;
export type DecideTherapistApprovalInput = z.infer<typeof decideTherapistApprovalSchema>;
export type RequestTherapistSessionTradeInput = z.infer<typeof requestTherapistSessionTradeSchema>;
export type CancelTherapistSessionTradeInput = z.infer<typeof cancelTherapistSessionTradeSchema>;
export type StartTherapistSessionInput = z.infer<typeof startTherapistSessionSchema>;
export type AcceptTherapistSessionTradeInput = z.infer<typeof acceptTherapistSessionTradeSchema>;
export type FinalizeTherapistCycleInput = z.infer<typeof finalizeTherapistCycleSchema>;
export type SecureTherapistSessionsResult = z.infer<typeof secureTherapistSessionsResultSchema>;
export type FinalizeTherapistCycleResult = z.infer<typeof finalizeTherapistCycleResultSchema>;
export type TherapistSessionErrorCode = z.infer<typeof therapistSessionErrorCodeSchema>;

/**
 * Report an Issue categories — TEND-060, from `Therapist Safety and Report an
 * Issue`'s approved list.
 *
 * ⚠ SEVEN CATEGORIES, FIVE DURATIONS. The spec's pause table covers Emergency,
 * Rider/Transport, Equipment and Technical at 30 minutes and Medical at 60.
 * `client_service`, `cash` and `other` are approved categories that state NO
 * pause — reporting a cash discrepancy should not take her out of Auto-Assign,
 * and giving them 30 minutes "for consistency" would invent an operational rule
 * (never-invent-policy.md). The server decides; the client never sends a
 * duration.
 */
export const therapistIssueCategorySchema = z.enum([
  "medical",
  "emergency",
  "rider_transport",
  "equipment",
  "technical",
  "client_service",
  "cash",
  "other",
]);

/**
 * File a report — TEND-060/062.
 *
 * No session id: the server resolves her live Session, if she has one, because
 * Report an Issue is "always available" and a valve that only works while on
 * duty is not always available.
 *
 * `note` is OPTIONAL and that is a rule, not a convenience: TEND-062 forbids
 * requiring evidence before immediate safety action. `occurredAt` exists because
 * she may report after reaching safety — "a delayed report after reaching safety
 * is not abandonment" — and the incident time is not the report time.
 */
export const reportTherapistIssueSchema = z
  .object({
    category: therapistIssueCategorySchema,
    note: z.string().trim().max(2000).optional(),
    occurredAt: z.string().datetime({ offset: true }).optional(),
  })
  .strict();

export type TherapistIssueCategory = z.infer<typeof therapistIssueCategorySchema>;
export type ReportTherapistIssueInput = z.infer<typeof reportTherapistIssueSchema>;

/**
 * The therapist SOS — 0273. Danger types from the 2026-09-25 backlog walkthrough
 * (Harassment · Violence · Medical · Unsafe location · Other). Every one cancels
 * her booking (0189); Medical gets the 60-minute medical pause, the rest 30.
 *
 * ⚠ THE TYPE IS OPTIONAL ON PURPOSE. "A therapist in danger must never be held at
 * a category picker": the SOS is sent without one and the type is added after
 * with `setTherapistSosTypeSchema`. Making it required here would be the bug.
 *
 * No location travels: the server snapshots her live pin itself, and a client
 * that could send a position could send a false one.
 */
export const therapistSosDangerTypeSchema = z.enum([
  "harassment",
  "violence",
  "medical",
  "unsafe_location",
  "other",
]);

export const raiseTherapistSosSchema = z
  .object({
    dangerType: therapistSosDangerTypeSchema.optional(),
    note: z.string().trim().max(2000).optional(),
  })
  .strict();

export const setTherapistSosTypeSchema = z
  .object({ alertId: uuidSchema, dangerType: therapistSosDangerTypeSchema })
  .strict();

/** Admin side — the alert stays on screen until someone acknowledges it. */
export const acknowledgeTherapistSosSchema = z.object({ alertId: uuidSchema }).strict();

/**
 * Admin side — close an acknowledged SOS with what happened (0277). The note is
 * required and free text: no categories exist to choose from. It is never
 * shown to the therapist.
 */
export const resolveTherapistSosSchema = z
  .object({ alertId: uuidSchema, note: z.string().trim().min(1).max(2000) })
  .strict();

/** What raising an SOS answers — shown to her so she knows it went through. */
export const raiseTherapistSosResultSchema = z.object({
  alertId: uuidSchema,
  bookingsCancelled: z.number().int().nonnegative(),
  paused: z.boolean(),
  pauseMinutes: z.number().int().positive().nullable(),
  notified: z.number().int().nonnegative(),
});

export type TherapistSosDangerType = z.infer<typeof therapistSosDangerTypeSchema>;
export type RaiseTherapistSosInput = z.infer<typeof raiseTherapistSosSchema>;
export type SetTherapistSosTypeInput = z.infer<typeof setTherapistSosTypeSchema>;
export type AcknowledgeTherapistSosInput = z.infer<typeof acknowledgeTherapistSosSchema>;
export type ResolveTherapistSosInput = z.infer<typeof resolveTherapistSosSchema>;
export type RaiseTherapistSosResult = z.infer<typeof raiseTherapistSosResultSchema>;

/**
 * Start a regular Pause — TEND-015.
 *
 * Presets are 30 and 60; a custom value is any positive number within the
 * remaining balance. The 60 here is the ceiling of the whole allowance, not of
 * one pause — the server checks the remainder, because only it knows what she
 * has already used.
 */
export const pauseTherapistSessionSchema = z
  .object({
    therapistSessionId: uuidSchema,
    minutes: z.number().int().positive().max(60),
  })
  .strict();

/**
 * End it early — TEND-015.
 *
 * No minutes: the server measures what she ACTUALLY used, because a client-sent
 * duration is a client-sent balance. Only actual time is deducted.
 */
export const resumeTherapistSessionSchema = z.object({ therapistSessionId: uuidSchema }).strict();

export type PauseTherapistSessionInput = z.infer<typeof pauseTherapistSessionSchema>;
export type ResumeTherapistSessionInput = z.infer<typeof resumeTherapistSessionSchema>;

/**
 * The four Equipment Availability toggles — TEND-012.
 *
 * ⚠ Which SERVICES need which equipment is not stated anywhere approved, and
 * the catalog has no Ear Candle or Hot Compress service at all. That mapping
 * lives in `service_equipment_requirements`, which ships empty (0105). These are
 * the four pieces themselves, which TEND-012 does name.
 */
export const therapistEquipmentSchema = z.enum([
  "ear_candle",
  "hot_compress",
  "ventosa_set",
  "hot_stone_set",
]);

/**
 * Declare one toggle.
 *
 * One at a time rather than a batch: each is independent (TEND-012 says
 * "independent" in as many words), and a batch would make a partial failure
 * ambiguous about which pieces she actually has.
 */
export const setTherapistEquipmentSchema = z
  .object({
    therapistSessionId: uuidSchema,
    equipment: therapistEquipmentSchema,
    available: z.boolean(),
  })
  .strict();

export type TherapistEquipment = z.infer<typeof therapistEquipmentSchema>;
export type SetTherapistEquipmentInput = z.infer<typeof setTherapistEquipmentSchema>;
